import { useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import TicketTableFilters from './TicketTableFilters'

/**
 * Buscador de tickets con criterios avanzados desplegables. Presentacional:
 * el estado vive en `useTicketFilters`.
 *
 * Se compone de dos partes para poder ubicarlas en una barra de herramientas:
 * la fila (buscador + botón de filtros + `children` a la derecha) y el panel
 * desplegable debajo.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.filters
 * @param {(key: string, value: string) => void} props.onChange
 * @param {() => void} props.onClear
 * @param {Object} props.options - Opciones de los selectores.
 * @param {number} props.activeCount - Criterios activos (incluida la búsqueda).
 * @param {string[]} [props.fields] - Criterios del panel avanzado.
 * @param {React.ReactNode} [props.children] - Acciones extra alineadas a la derecha.
 * @returns {JSX.Element}
 */
const TicketSearchBar = ({
  filters,
  onChange,
  onClear,
  options,
  activeCount,
  fields,
  children,
}) => {
  const [open, setOpen] = useState(false)
  // La búsqueda de texto ya se ve en el campo; el indicador cuenta el resto.
  const advancedCount = activeCount - (filters.search?.trim() ? 1 : 0)

  return (
    <div className="tsb">
      <div className="tsb-row">
        <div className="tsb-search">
          <i className="fa-solid fa-magnifying-glass" />
          <input
            type="search"
            placeholder="Buscar por ID, título, autor o responsable…"
            value={filters.search}
            onChange={(e) => onChange('search', e.target.value)}
            aria-label="Buscar tickets"
          />
        </div>

        <button
          type="button"
          className={`tsb-btn ${open ? 'is-open' : ''} ${advancedCount ? 'has-filters' : ''}`}
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
        >
          <i className="fa-solid fa-sliders" />
          <span>Filtros</span>
          {advancedCount > 0 && (
            <span className="tsb-count">{advancedCount}</span>
          )}
        </button>

        {activeCount > 0 && (
          <button
            type="button"
            className="tsb-clear"
            onClick={onClear}
            title="Quitar búsqueda y filtros"
          >
            <i className="fa-solid fa-xmark" />
          </button>
        )}

        {children && <div className="tsb-actions">{children}</div>}
      </div>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.18 }}
            style={{ overflow: 'hidden' }}
          >
            <div className="tsb-panel">
              <TicketTableFilters
                filters={filters}
                onChange={onChange}
                onClear={onClear}
                options={options}
                fields={fields}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <style>{`
        .tsb-row { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; }
        .tsb-search {
          flex: 1 1 240px; max-width: 380px; display: flex; align-items: center; gap: 8px;
          height: 34px; padding: 0 12px; border-radius: 8px;
          border: 1px solid var(--gm-border); background: var(--gm-surface);
          color: var(--gm-text-faint); transition: border-color 0.15s ease;
        }
        .tsb-search:focus-within { border-color: var(--gm-primary); }
        .tsb-search i { font-size: 0.8rem; }
        .tsb-search input {
          flex: 1; min-width: 0; border: none; outline: none; background: transparent;
          font-size: 0.85rem; color: var(--gm-text);
        }
        .tsb-btn, .tsb-clear {
          height: 34px; display: inline-flex; align-items: center; gap: 6px;
          border-radius: 8px; border: 1px solid var(--gm-border); background: var(--gm-surface);
          color: var(--gm-text-muted); font-size: 0.82rem; font-weight: 500; padding: 0 12px;
        }
        .tsb-btn:hover, .tsb-clear:hover { color: var(--gm-text); }
        .tsb-btn.is-open, .tsb-btn.has-filters { border-color: var(--gm-primary); color: var(--gm-primary); }
        .tsb-clear { padding: 0 10px; }
        .tsb-count {
          min-width: 18px; height: 18px; padding: 0 5px; border-radius: 99px;
          background: var(--gm-primary); color: #fff; font-size: 0.65rem; font-weight: 700;
          display: inline-flex; align-items: center; justify-content: center;
        }
        .tsb-actions { margin-left: auto; display: flex; flex-wrap: wrap; align-items: center; gap: 8px; }
        .tsb-panel {
          margin-top: 8px; padding: 12px; border-radius: 10px;
          background: var(--gm-surface); border: 1px solid var(--gm-border);
        }
      `}</style>
    </div>
  )
}

export default TicketSearchBar
